import { supabase } from "../../supabase/supabase";

/**
 * The do-not-call list (public.do_not_call_numbers, kmhari/voxi-api-server#28), at two levels:
 * organization (assistant_id NULL: no assistant of the organization calls the number) and agent
 * (assistant_id set: only that assistant does not). Agent rows are written when someone asks on a
 * call not to be called again, through the API, or by hand with an agent picked.
 *
 * By hand, only a lead's number is added (kmhari/voxi-frontend#103): the dashboard picks one of the
 * organization's leads, and the row keeps the lead it came from (lead_id, migration 20261029150000).
 *
 * A listed number is refused by the API server before it is dialled, and listing it cancels its
 * pending calls, the agent's only for an agent row (migration 20261028120000). RLS: members and
 * platform admins read and add by hand; owners, admins and platform admins remove.
 */
export type DoNotCallSource = "manual" | "candidate_request" | "api";

export interface DoNotCallNumber {
  id: string;
  phone_number: string;
  organization_id: string;
  reason: string | null;
  source: DoNotCallSource;
  source_call_id: string | null;
  assistant_id: string | null;
  /** The lead a number added by hand came from; null for other sources and older rows. */
  lead_id: string | null;
  created_at: string;
  /** The agent's name, for an agent-level row. */
  assistant: { name: string } | null;
  /** The lead's name, when the row came from a lead (null if the lead was deleted). */
  lead: { first_name: string | null; last_name: string | null } | null;
}

/** A lead offered by the picker, with its number as it would be listed. */
export interface DoNotCallLead {
  id: string;
  name: string;
  phone_number: string;
  /** The number in E.164 when it is a real number for its country, else null (cannot be added). */
  e164: string | null;
}

/** How one number is listed in one organization: for every agent, and/or for some agents. */
export interface DoNotCallListing {
  organization: boolean;
  assistantIds: Set<string>;
}

export interface DoNotCallAssistant {
  id: string;
  name: string;
}

export const DO_NOT_CALL_SOURCE_LABELS: Record<DoNotCallSource, string> = {
  manual: "Added by hand",
  candidate_request: "Asked on a call",
  api: "API",
};

const COLUMNS =
  "id, phone_number, organization_id, reason, source, source_call_id, assistant_id, lead_id, created_at, assistant:assistants(name), lead:leads(first_name, last_name)";

// The table's CHECK (do_not_call_numbers_phone_number_check).
const E164 = /^\+[1-9][0-9]{7,14}$/;

/**
 * A dialled number in E.164, or null when it has no digits. Same rule as the API server
 * (to_e164), voxi-2-0 (toE164) and the database (do_not_call_e164): numbers are Indian unless
 * they carry a country code.
 */
export function toE164(number: string | null | undefined): string | null {
  const raw = (number ?? "").trim();
  let digits = raw.replace(/\D/g, "");
  if (!digits) return null;
  if (raw.startsWith("+")) return dropIndianTrunkZero(`+${digits}`);
  if (digits.startsWith("00") && digits.length > 11) return dropIndianTrunkZero(`+${digits.slice(2)}`);
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `+91${digits.slice(1)}`;
  if (digits.startsWith("0") && digits.length > 11) digits = digits.slice(1);
  return dropIndianTrunkZero(`+${digits}`);
}

function dropIndianTrunkZero(e164: string): string {
  return e164.startsWith("+910") && e164.length === 14 ? `+91${e164.slice(4)}` : e164;
}

/** A number the table will accept, in E.164, or null. Checks the shape only (for lookups). */
export function validE164(number: string | null | undefined): string | null {
  const e164 = toE164(number);
  return e164 && E164.test(e164) ? e164 : null;
}

/**
 * A checker for real phone numbers: the number in its canonical E.164 form when it is a real
 * number for its country, else null. Canonical: a domestic trunk '0' kept after the country code
 * is dropped ('+44 (0)7911 123456' gives '+447911123456'), the form a dialled number has, which
 * the dial check matches exactly. The full metadata ("max"); the default build checks the length
 * only and would take e.g. +98378633. The API server checks the same way (libphonenumber metadata),
 * so both agree on what can be added. Loaded on first use, so the pages that import this service
 * only for their badges (Leads, Call Logs) do not download it.
 */
async function realNumberChecker(): Promise<(number: string | null | undefined) => string | null> {
  const { parsePhoneNumberFromString } = await import("libphonenumber-js/max");
  return (number) => {
    const e164 = validE164(number);
    const parsed = e164 ? parsePhoneNumberFromString(e164) : undefined;
    // The table's CHECK takes 8 to 15 digits: a real number outside that cannot be stored.
    return parsed?.isValid() && E164.test(parsed.number) ? parsed.number : null;
  };
}

export const leadName = (lead: { first_name?: string | null; last_name?: string | null } | null | undefined) =>
  [lead?.first_name, lead?.last_name].map((part) => (part ?? "").trim()).filter(Boolean).join(" ") || "Unnamed lead";

/**
 * Folds listing rows into one DoNotCallListing per key: an organization row sets `organization`,
 * an agent row adds its assistant. Used by the badges (keyed by organization and number) and by
 * the Add dialog (keyed by number).
 */
export function addListings<R extends { assistant_id: string | null }>(
  listings: Map<string, DoNotCallListing>,
  rows: R[],
  keyOf: (row: R) => string,
): Map<string, DoNotCallListing> {
  for (const row of rows) {
    const key = keyOf(row);
    const listing = listings.get(key) ?? { organization: false, assistantIds: new Set<string>() };
    if (row.assistant_id) listing.assistantIds.add(row.assistant_id);
    else listing.organization = true;
    listings.set(key, listing);
  }
  return listings;
}

/** The key the badges look a number up by: one organization's listing of one number. */
export const doNotCallKey = (organizationId: string | null | undefined, number: string | null | undefined) =>
  `${organizationId ?? ""}|${toE164(number) ?? ""}`;

export const doNotCallService = {
  /** One organization's numbers, newest first. */
  async getNumbers(organizationId: string): Promise<DoNotCallNumber[]> {
    const { data, error } = await supabase
      .from("do_not_call_numbers")
      .select(COLUMNS)
      .eq("organization_id", organizationId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    // The generated types predate assistant_id, so they type the embedded assistant as a list;
    // it is one row (many-to-one), as DoNotCallNumber says.
    return (data as unknown as DoNotCallNumber[]) || [];
  },

  /**
   * How these (organization, number) pairs are listed, for the badges on call and lead lists, by
   * doNotCallKey(). Asks only about the numbers on the page, one request per organization, through
   * do_not_call_listed_numbers(). An error gives an empty map: a missing badge must never break a
   * list.
   */
  async getListings(
    items: { organizationId: string | null | undefined; phoneNumber: string | null | undefined }[],
  ): Promise<Map<string, DoNotCallListing>> {
    const byOrganization = new Map<string, Set<string>>();
    for (const { organizationId, phoneNumber } of items) {
      const e164 = validE164(phoneNumber);
      if (!organizationId || !e164) continue;
      if (!byOrganization.has(organizationId)) byOrganization.set(organizationId, new Set());
      byOrganization.get(organizationId)!.add(e164);
    }
    const listings = new Map<string, DoNotCallListing>();
    await Promise.all(
      [...byOrganization].map(async ([organizationId, numbers]) => {
        const { data, error } = await supabase.rpc("do_not_call_listed_numbers", {
          p_organization_id: organizationId,
          p_numbers: [...numbers],
        });
        if (error) {
          console.error("Error checking the do-not-call list:", error);
          return;
        }
        const rows = (data as { phone_number: string; scope: string; assistant_id: string | null }[]) || [];
        addListings(listings, rows, (row) => doNotCallKey(organizationId, row.phone_number));
      }),
    );
    return listings;
  },

  /** Owners, admins and platform admins remove numbers (the table's RLS); members only add them. */
  async canRemove(organizationId: string): Promise<boolean> {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return false;
    const [{ data: member }, { data: platformAdmin }] = await Promise.all([
      supabase
        .from("organization_members")
        .select("role")
        .eq("user_id", userId)
        .eq("organization_id", organizationId)
        .maybeSingle(),
      supabase.rpc("is_platform_admin"),
    ]);
    return ["owner", "admin"].includes(member?.role ?? "") || platformAdmin === true;
  },

  /**
   * The organization's leads whose name or number matches the search, for the picker (at most 20,
   * newest first), through do_not_call_search_leads(): a number is compared on its digits only, so
   * '98400 12345' finds a lead stored as '+91 98400 12345'.
   */
  async searchLeads(organizationId: string, query: string): Promise<DoNotCallLead[]> {
    const { data, error } = await supabase.rpc("do_not_call_search_leads" as never, {
      p_organization_id: organizationId,
      p_query: query.trim(),
    } as never);
    if (error) throw error;
    const realE164 = await realNumberChecker();
    return ((data as unknown as { id: string; first_name: string | null; last_name: string | null; phone_number: string }[]) || []).map(
      (lead) => ({
        id: lead.id,
        name: leadName(lead),
        phone_number: lead.phone_number,
        e164: realE164(lead.phone_number),
      }),
    );
  },

  /** The organization's assistants, for the agent picker. */
  async getAssistants(organizationId: string): Promise<DoNotCallAssistant[]> {
    const { data, error } = await supabase
      .from("assistants")
      .select("id, name")
      .eq("organization_id", organizationId)
      .order("name");
    if (error) throw error;
    return (data as DoNotCallAssistant[]) || [];
  },

  /**
   * Add a lead's number for the whole organization (assistantIds null), or for each of the given
   * agents, in one save. Only a real number for its country is added. A listing that already exists
   * (added meanwhile, e.g. by a call) is skipped, not an error: `skipped` names those agents.
   * Throws a readable message.
   */
  async addLead(
    organizationId: string,
    lead: Pick<DoNotCallLead, "id" | "name" | "phone_number">,
    reason: string | undefined,
    assistantIds: string[] | null,
  ): Promise<{ added: DoNotCallNumber[]; skipped: (string | null)[]; failed: (string | null)[] }> {
    const e164 = (await realNumberChecker())(lead.phone_number);
    if (!e164) throw new Error(`${lead.name}'s number (${lead.phone_number}) is not a valid phone number`);
    if (assistantIds !== null && assistantIds.length === 0) throw new Error("Pick at least one agent");
    const { data: userData } = await supabase.auth.getUser();
    const row = (assistantId: string | null) => ({
      phone_number: e164,
      organization_id: organizationId,
      assistant_id: assistantId,
      lead_id: lead.id,
      reason: reason?.trim() || null,
      source: "manual",
      created_by: userData.user?.id ?? null,
    });
    const scopes: (string | null)[] = assistantIds === null ? [null] : [...new Set(assistantIds)];

    // One insert for every scope: all or nothing.
    const { data, error } = await supabase
      .from("do_not_call_numbers")
      .insert(scopes.map(row) as never)
      .select(COLUMNS);
    if (!error) return { added: (data as unknown as DoNotCallNumber[]) || [], skipped: [], failed: [] };
    if (error.code !== "23505") throw error;

    // One of them is already listed (the unique index refused the whole insert): add the others
    // one by one, skip the ones that are listed, and report any that fail, so the agents already
    // saved are never hidden behind an error.
    const added: DoNotCallNumber[] = [];
    const skipped: (string | null)[] = [];
    const failed: (string | null)[] = [];
    let firstError: unknown = null;
    for (const scope of scopes) {
      const { data: one, error: oneError } = await supabase
        .from("do_not_call_numbers")
        .insert(row(scope) as never)
        .select(COLUMNS)
        .single();
      if (oneError?.code === "23505") skipped.push(scope);
      else if (oneError) {
        failed.push(scope);
        firstError ??= oneError;
      } else added.push(one as unknown as DoNotCallNumber);
    }
    if (added.length === 0) {
      if (firstError) throw firstError;
      throw new Error(`${e164} is already on the do-not-call list${assistantIds ? " for every agent picked" : ""}`);
    }
    return { added, skipped, failed };
  },

  /**
   * Remove a listing, so the organization (or the agent) can call the number again (owners, admins
   * and platform admins).
   * RLS refuses a delete by matching no row, not by an error, so an empty result is refused here.
   */
  async removeNumber(id: string): Promise<void> {
    const { data, error } = await supabase.from("do_not_call_numbers").delete().eq("id", id).select("id");
    if (error) throw error;
    if (!data || data.length === 0) throw new Error("Only owners, admins and super admins can remove a number");
  },
};
